import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Pencil, X, Check, Loader2, Trash2,
} from "lucide-react";
import {
  fetchInspectionReference,
  createInspectionOption,
  updateInspectionOption,
  deleteInspectionOption,
  createInspectionCategory,
  updateInspectionCategory,
  deleteInspectionCategory,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errorMessage } from "@/lib/errors";

const GROUPS = ["Inspection", "Running", "Complications"];

/**
 * Inspection Setup page — manage categories + their result/action options.
 *
 * Renaming an option only updates the option's label (the inspection records
 * store option_id, never the label) so changes propagate everywhere instantly
 * — no cascade needed.
 */
export default function InspectionSetup() {
  const [reference, setReference] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = async (preserveActive = true) => {
    setLoading(true);
    try {
      const r = await fetchInspectionReference();
      setReference(r);
      if (!preserveActive && r.categories?.length && !activeId) {
        setActiveId(r.categories[0].id);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(GROUPS.map((g) => [g, []]));
    for (const c of reference.categories || []) {
      const g = GROUPS.includes(c.group) ? c.group : "Inspection";
      out[g].push(c);
    }
    return out;
  }, [reference]);

  const active = (reference.categories || []).find((c) => c.id === activeId);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 text-[#0A0604]" data-testid="inspection-setup">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/data" className="text-sm text-[#3A2A10] hover:text-[#0A0604] flex items-center gap-1">
          <ChevronLeft size={16} /> Data
        </Link>
        <h1 className="font-serif-display text-3xl">Inspection Setup</h1>
      </div>

      <p className="text-sm text-[#3A2A10] mb-6 max-w-3xl">
        Categories are what you inspect (Crystal, Mainspring, etc.). Each has a list of
        possible <strong>Results</strong> (Normal, Cracked, Worn out…) and <strong>Actions</strong>
        (Replace mainspring, Polish steel…). Action lists start empty so you can build them as you go.
        Renaming an option updates every inspection that uses it instantly.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Left — category list */}
        <aside className="border border-[#8B6A1F]/35 rounded-lg bg-[#FBF3D9]/60 overflow-hidden h-fit md:sticky md:top-6" data-testid="insp-cat-list">
          {GROUPS.map((g) => {
            const cats = grouped[g] || [];
            if (!cats.length) return null;
            return (
              <div key={g}>
                <div className="px-4 py-2 bg-[#FBF3D9] text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] border-b border-[#8B6A1F]/35">
                  {g}
                </div>
                <ul className="divide-y divide-[#8B6A1F]/15">
                  {cats.map((c) => {
                    const isActive = c.id === activeId;
                    const resN = c.result_options?.length || 0;
                    const actN = c.action_options?.length || 0;
                    return (
                      <li key={c.id}>
                        <button
                          onClick={() => setActiveId(c.id)}
                          className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${
                            isActive ? "bg-[#D4AF37]/15" : "hover:bg-[#F5E9C5]/60"
                          }`}
                          data-testid={`insp-cat-${c.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm text-[#0A0604] truncate">{c.name}</div>
                            <div className="text-[10px] text-[#3A2A10]/80 font-mono-data">
                              {c.kind === "reading" ? "reading" : `${resN} result · ${actN} action`}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <div className="px-4 py-3 border-t border-[#8B6A1F]/35">
            <NewCategoryForm onCreated={() => load(true)} />
          </div>
        </aside>

        {/* Right — editor for active category */}
        <main className="border border-[#8B6A1F]/35 rounded-lg bg-[#FBF3D9]/60 overflow-hidden">
          {loading && !active ? (
            <div className="p-10 flex items-center gap-2 text-sm text-[#3A2A10]">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : !active ? (
            <div className="p-10 text-sm text-[#3A2A10]">Pick a category on the left.</div>
          ) : (
            <CategoryEditor category={active} onChange={() => load(true)} />
          )}
        </main>
      </div>
    </div>
  );
}

function NewCategoryForm({ onCreated }) {
  const [name, setName] = useState("");
  const [group, setGroup] = useState("Inspection");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createInspectionCategory({ name: name.trim(), group, kind: "result" });
      setName("");
      onCreated();
    } catch (err) {
      toast.error(errorMessage(err, "Add failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="New category…"
        className="dark-input h-8 text-xs"
        data-testid="insp-new-cat-name"
      />
      <select
        value={group}
        onChange={(e) => setGroup(e.target.value)}
        className="dark-input h-8 text-xs px-2 rounded-sm"
      >
        {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
      </select>
      <Button type="submit" size="sm" disabled={!name.trim() || busy} className="h-8 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" data-testid="insp-new-cat-add">
        <Plus size={12} className="mr-1" /> Add category
      </Button>
    </form>
  );
}

function CategoryEditor({ category, onChange }) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(category.name);

  useEffect(() => { setNameDraft(category.name); setEditingName(false); }, [category.id, category.name]);

  const renameCategory = async () => {
    const v = nameDraft.trim();
    if (!v || v === category.name) { setEditingName(false); return; }
    try {
      await updateInspectionCategory(category.id, { name: v });
      toast.success("Category renamed");
      setEditingName(false);
      onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Rename failed"));
    }
  };

  const archiveCategory = async () => {
    if (!window.confirm(`Archive category "${category.name}"? It will hide from the inspection sheet but existing inspections keep referencing it.`)) return;
    try {
      await deleteInspectionCategory(category.id);
      toast.success("Archived");
      onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Archive failed"));
    }
  };

  return (
    <div>
      <header className="flex items-center justify-between px-5 py-3 border-b border-[#8B6A1F]/35 bg-[#FBF3D9] gap-2">
        {editingName ? (
          <form onSubmit={(e) => { e.preventDefault(); renameCategory(); }} className="flex items-center gap-2 flex-1">
            <Input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setEditingName(false); }}
              className="dark-input h-8 max-w-sm"
              data-testid="insp-cat-rename-input"
            />
            <Button type="submit" size="sm" className="h-8 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]">
              <Check size={14} />
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditingName(false)} className="h-8 text-[#3A2A10]">
              <X size={14} />
            </Button>
          </form>
        ) : (
          <div className="flex items-center gap-3">
            <h2 className="font-serif-display text-2xl">{category.name}</h2>
            <span className="text-[10px] uppercase tracking-wider text-[#3A2A10] px-1.5 py-0.5 bg-[#F5E9C5] rounded">
              {category.group}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#3A2A10]/70">{category.kind}</span>
          </div>
        )}
        {!editingName && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditingName(true)} className="h-8 text-[#3A2A10]" data-testid="insp-cat-rename">
              <Pencil size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={archiveCategory} className="h-8 text-rose-700" data-testid="insp-cat-archive">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </header>

      {category.kind === "reading" ? (
        <div className="p-6 text-sm text-[#3A2A10]">
          This category captures a numeric reading (no result/action options).
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
          <OptionList
            title="Result options"
            categoryId={category.id}
            kind="result"
            options={category.result_options || []}
            onChange={onChange}
          />
          <OptionList
            title="Action options"
            categoryId={category.id}
            kind="action"
            options={category.action_options || []}
            onChange={onChange}
            emptyHint="No actions yet. Add the workshop steps that fix this category's issues — e.g. 'Replace mainspring', 'Polish steel'."
          />
        </div>
      )}
    </div>
  );
}

function OptionList({ title, categoryId, kind, options, onChange, emptyHint }) {
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(null);

  const add = async (e) => {
    e?.preventDefault?.();
    const v = newLabel.trim();
    if (!v) return;
    setBusy("__add__");
    try {
      await createInspectionOption(categoryId, { label: v, kind });
      setNewLabel("");
      onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Add failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">{title}</div>
      <ul className="divide-y divide-[#8B6A1F]/15 border border-[#8B6A1F]/30 rounded-sm bg-[#FBF3D9]/40">
        {options.length === 0 ? (
          <li className="px-3 py-3 text-xs text-[#3A2A10]/80 italic">
            {emptyHint || "No options yet."}
          </li>
        ) : (
          options.map((o) => (
            <OptionRow
              key={o.id}
              option={o}
              busy={busy === o.id}
              onBusy={(b) => setBusy(b ? o.id : null)}
              onChange={onChange}
            />
          ))
        )}
      </ul>
      <form onSubmit={add} className="flex gap-2 mt-2" data-testid={`insp-opt-add-${kind}`}>
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={`New ${kind}…`}
          className="dark-input h-8 text-xs flex-1"
        />
        <Button type="submit" size="sm" className="h-8 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={!newLabel.trim() || busy === "__add__"}>
          <Plus size={12} />
        </Button>
      </form>
    </div>
  );
}

function OptionRow({ option, busy, onBusy, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(option.label);
  useEffect(() => { setDraft(option.label); setEditing(false); }, [option.id, option.label]);

  const rename = async () => {
    const v = draft.trim();
    if (!v || v === option.label) { setEditing(false); return; }
    onBusy(true);
    try {
      await updateInspectionOption(option.id, { label: v });
      toast.success("Renamed");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Rename failed"));
    } finally {
      onBusy(false);
    }
  };
  const archive = async () => {
    if (!window.confirm(`Archive "${option.label}"? Existing inspections referencing it keep working.`)) return;
    onBusy(true);
    try {
      await deleteInspectionOption(option.id);
      toast.success("Archived");
      onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Archive failed"));
    } finally {
      onBusy(false);
    }
  };

  return (
    <li className="px-3 py-2 flex items-center justify-between gap-2">
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); rename(); }} className="flex items-center gap-2 flex-1">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
            className="dark-input h-7 text-xs"
          />
          <Button type="submit" size="sm" className="h-7 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10]" disabled={busy}>
            <Check size={12} />
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-[#3A2A10]">
            <X size={12} />
          </Button>
        </form>
      ) : (
        <>
          <span className="text-sm text-[#0A0604] truncate" data-testid={`insp-opt-${option.id}`}>
            {option.label}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 text-[#3A2A10]" disabled={busy}>
              <Pencil size={12} />
            </Button>
            <Button variant="ghost" size="sm" onClick={archive} className="h-7 text-rose-700" disabled={busy}>
              <Trash2 size={12} />
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
